import { useState } from 'react';
import { api, type BankCell, type DriverImportTab, type DriverSheetSync } from '../lib/api';
import { ErrorBox } from '../components/DataTable';

/**
 * 기사개인오더 계산서 가져오기(#12 5번 · #14 3번). 기획 `plan/accounting.md` 5-H.
 * 기사접수SYS(dytaxdriver 시트)를 엑셀로 내려받아 올리면 `세금_*` 탭만 읽어 번호 기준으로 맞춘다.
 * 올리기 전에 미리보기(서버가 실제로 해 보고 되돌린 숫자) → 「적용」.
 */
/**
 * 시트 새 신청 **자동 가져오기**(서버 10분마다 · 06~23시 · #164)의 마지막 결과 한 줄. 세금계산서·4종 두 화면이 같이 쓴다.
 * 🔴 멈추면 조용히 묵는다 — 운영 시간에 30분 넘게 안 돌았으면 노랗게 알린다.
 */
export function DriverSyncLine({ sync, open }: { sync: DriverSheetSync | null | undefined; open: boolean | null | undefined }) {
  if (open) return null;
  const kst = new Date(Date.now() + 9 * 3600_000);
  const hour = kst.getUTCHours();
  const ageMin = sync?.at ? Math.round((kst.getTime() - Date.parse(sync.at.replace(' ', 'T') + ':00Z')) / 60_000) : null;
  const stale = hour >= 7 && // 06시 첫 회차 전엔 전날 밤 값이 정상
    (ageMin === null || ageMin > 30);
  const bad = sync?.ok === false || stale;
  return (
    <p className={`rounded-lg px-3 py-2 text-xs ${bad ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>
      시트 새 신청은 <b>10분마다 자동으로</b> 가져옵니다(06~23시).{' '}
      {sync?.at ? <>마지막 {sync.at.slice(5)} — {sync.note || '변화 없음'}</> : '아직 한 번도 돌지 않았습니다.'}
      {stale && sync?.at && ' · 30분 넘게 안 돌았습니다 — 버튼으로 직접 가져오고 관리자에게 알려 주세요.'}
    </p>
  );
}

const TABS = ['세금_동영(파주)', '세금_동영(용인)', '세금_동영투어', '세금_동영(서울)', '세금_동영(이천)'];

export default function DriverInvoiceImport({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [tabs, setTabs] = useState<{ name: string; rows: BankCell[][] }[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<DriverImportTab[] | null>(null);
  const [result, setResult] = useState<DriverImportTab[] | null>(null);
  // 'sheet' = dytaxdriver GAS 에서 바로(②단계) · 'file' = 엑셀 올리기
  const [from, setFrom] = useState<'file' | 'sheet'>('file');
  const [writeBack, setWriteBack] = useState<{ asked: number; updated: number; missing: string[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy('파일 읽는 중…'); setError(null); setPreview(null); setResult(null);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const got = wb.SheetNames.filter((n) => TABS.includes(n)).map((name) => ({
        name, rows: XLSX.utils.sheet_to_json<BankCell[]>(wb.Sheets[name]!, { header: 1, defval: '', raw: true, blankrows: false }).map((r) => r.slice(0, 23)),
      }));
      if (got.length === 0) throw new Error(`기사접수 탭(${TABS.join('·')})이 없습니다. 「세금계산서-기사접수SYS」를 엑셀로 내려받은 파일인지 확인해 주세요.`);
      setTabs(got); setFileName(file.name); setFrom('file');
      setBusy('확인 중…');
      setPreview((await api.importDriverInvoices({ fileName: file.name, tabs: got })).tabs);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err)); setTabs(null);
    } finally { setBusy(null); e.target.value = ''; }
  }

  async function fromSheet() {
    setBusy('기사접수 시트 읽는 중…'); setError(null); setPreview(null); setResult(null); setWriteBack(null);
    try {
      const r = await api.syncDriverInvoices({});
      setFrom('sheet'); setFileName(r.fileName ?? '기사접수 시트'); setPreview(r.tabs);
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setBusy(null); }
  }

  async function commit() {
    if (from === 'sheet') {
      setBusy('적용·되쓰기 중…'); setError(null);
      try {
        const r = await api.syncDriverInvoices({ commit: true });
        setResult(r.tabs); setWriteBack(r.writeBack); setPreview(null); onDone();
      } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setBusy(null); }
      return;
    }
    if (!tabs) return;
    setBusy('적용 중…'); setError(null);
    try {
      setResult((await api.importDriverInvoices({ fileName, tabs, commit: true })).tabs);
      setPreview(null); setTabs(null); onDone();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); } finally { setBusy(null); }
  }

  const table = (rows: DriverImportTab[]) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-ink-soft">
          {['법인', '번호', '새로', '바뀜', '그대로', '수정(−) 줄', '지움', '안 넣음(신청취소)', '건너뜀', '충돌'].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.tab} className="border-b">
              <td className="px-2 py-1.5">{t.corpName}</td><td className="px-2">{t.numbers.toLocaleString()}</td>
              <td className="px-2 font-medium text-emerald-700">{t.added.toLocaleString()}</td><td className="px-2">{t.updated.toLocaleString()}</td>
              <td className="px-2 text-ink-faint">{t.unchanged.toLocaleString()}</td><td className="px-2">{t.amended}</td>
              <td className="px-2">{t.removed}</td><td className="px-2 text-ink-faint">{t.dropped}</td>
              <td className="px-2" title={t.skipped.join('\n')}>{t.skipped.length || ''}</td>
              <td className="px-2 text-red-700" title={t.conflicts.join('\n')}>{t.conflicts.length || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.some((t) => t.conflicts.length) && (
        <p className="mt-2 text-xs text-red-700">충돌 = 입금이 연결된 계산서인데 시트에서 금액이 바뀌었거나 취소됐습니다. 그 줄은 건드리지 않았습니다 —
          {' '}{rows.flatMap((t) => t.conflicts).slice(0, 5).join(', ')}</p>
      )}
    </div>
  );

  return (
    <div className="card space-y-3 p-5">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold">기사 새 신청 가져오기</h2>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
      <p className="text-sm text-ink-soft">
        기사개인오더 계산서는 <b>이 웹이 정본</b>입니다 — 내용·발행자·입금일자는 발행대장에서 고칩니다.
        기사들이 아직 기사접수 앱(시트)으로 신청하는 동안만, 여기서 <b>새 신청과 기사 취소</b>를 들여옵니다(이미 있는 번호는 웹 값을 그대로 둡니다).
        적용하면 웹의 발행자·입금일자를 시트에 맞춰 적어 기사 앱의 「발행완료·입금완료」 표시가 맞게 보입니다.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-primary" onClick={fromSheet} disabled={busy !== null}>
          {busy ?? '새 신청 확인'}
        </button>
      </div>
      {error && <ErrorBox message={error} />}
      {preview && (
        <div className="space-y-2">
          <p className="text-sm">미리보기 — <b>{fileName}</b> (아직 반영 안 됨)</p>
          {table(preview)}
          <button className="btn-primary" onClick={commit} disabled={busy !== null || (from === 'file' && preview.every((t) => !t.added && !t.updated && !t.amended && !t.removed))}
                  title={from === 'sheet' ? '새 신청이 없어도 적용하면 발행자·입금일자를 시트에 맞춥니다' : undefined}>
            적용
          </button>
        </div>
      )}
      {result && (
        <div className="space-y-2">
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">반영했습니다.
            {writeBack && <> 시트에 발행자·입금일자 맞춤 <b>{writeBack.updated}</b>건{writeBack.missing.length > 0 && <> · 시트에 번호 없음 {writeBack.missing.length}</>}.</>}
          </p>
          {table(result)}
        </div>
      )}
    </div>
  );
}
